import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton, Skeleton, EmptyState, Segmented } from "@/components/ios";
import { ChevronLeft, Map, Upload, Save, MapPin, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const SEV_COLOR = { none: "var(--text-secondary)", minor: "var(--warning)", moderate: "var(--warning)", major: "var(--danger)" };
const WALLS = [{ value: "N", label: "N" }, { value: "E", label: "E" }, { value: "S", label: "S" }, { value: "W", label: "W" }];

export default function FloorPlanPin() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rep, setRep] = useState(null);
  const [plan, setPlan] = useState(undefined); // undefined=loading, null=none, obj
  const [pins, setPins] = useState([]);
  const [selected, setSelected] = useState(null); // item_key being placed / active
  const [dirty, setDirty] = useState(false);
  const imgRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/reports/${id}`).then(({ data }) => {
      setRep(data);
      setPins(data.plan_pins || []);
      api.get(`/properties/${data.property_id}/floorplan`).then(({ data: fp }) => setPlan(fp));
    });
  }, [id]);

  const uploadPlan = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post(`/properties/${rep.property_id}/floorplan`, { data_url: reader.result });
        toast.success(`Floor plan uploaded (v${data.version})`);
        const { data: fp } = await api.get(`/properties/${rep.property_id}/floorplan`);
        setPlan(fp);
      } catch (e2) { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const placePin = (e) => {
    if (!selected) { toast("Select a finding first, then tap the plan", { icon: <MapPin size={16} /> }); return; }
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    const f = rep.findings.find((ff) => ff.item_key === selected);
    setPins((prev) => {
      const others = prev.filter((p) => p.item_key !== selected);
      const existing = prev.find((p) => p.item_key === selected);
      return [...others, { item_key: selected, label: f.label, room: f.room, severity: f.severity, x, y, wall: existing?.wall || "N" }];
    });
    setDirty(true);
  };

  const setWall = (item_key, wall) => { setPins((prev) => prev.map((p) => p.item_key === item_key ? { ...p, wall } : p)); setDirty(true); };
  const removePin = (item_key) => { setPins((prev) => prev.filter((p) => p.item_key !== item_key)); setDirty(true); };

  const save = async () => {
    await api.put(`/reports/${id}/pins`, { pins, floorplan_id: plan?.id });
    setDirty(false);
    toast.success("Findings map saved");
  };

  if (!rep || plan === undefined) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  const findings = rep.findings || [];
  const pinnedKeys = new Set(pins.map((p) => p.item_key));

  return (
    <div className="pb-16">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPlan} data-testid="floorplan-input" />
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => nav(`/reports/${id}/review`)} className="flex items-center gap-1 text-sm text-secondary"><ChevronLeft size={16} /> Report</button>
        <div className="flex gap-2">
          {plan && <AButton variant="text" onClick={() => fileRef.current?.click()}><Upload size={14} className="inline mr-1" /> Replace plan</AButton>}
          {dirty && <AButton testid="save-pins" onClick={save}><Save size={14} className="inline mr-1" /> Save map</AButton>}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-1">
        <Map className="accent-text" size={26} />
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Floor Plan & Findings Map</h1>
      </div>
      <p className="text-sm text-secondary mb-5">{rep.property?.address} · pin each finding to its room and wall.</p>

      {!plan ? (
        <Surface><EmptyState icon={Map} title="No floor plan yet"
          subtitle="Upload a plan image (PDF export or photo) to pin findings to room/wall positions."
          action={<AButton testid="upload-floorplan" onClick={() => fileRef.current?.click()}><Upload size={15} className="inline mr-1.5" /> Upload floor plan</AButton>} /></Surface>
      ) : (
        <div className="grid lg:grid-cols-[1fr_300px] gap-5">
          {/* plan canvas */}
          <Surface className="p-3 overflow-hidden">
            <div className="relative select-none" style={{ cursor: selected ? "crosshair" : "default" }}>
              <img ref={imgRef} src={mediaUrl(plan.signed_url)} onClick={placePin} data-testid="floorplan-canvas"
                className="w-full rounded-xl" alt="floor plan" draggable={false} />
              {pins.map((p, i) => (
                <motion.button key={p.item_key} initial={{ scale: 0 }} animate={{ scale: 1 }}
                  onClick={(e) => { e.stopPropagation(); setSelected(p.item_key); }}
                  data-testid={`pin-${p.item_key}`}
                  className="absolute -translate-x-1/2 -translate-y-full flex flex-col items-center"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}>
                  <div className="h-6 w-6 rounded-full grid place-items-center text-white text-[10px] font-bold ring-2 ring-white shadow-lg"
                    style={{ background: SEV_COLOR[p.severity] || "var(--accent)", outline: selected === p.item_key ? "2px solid var(--accent)" : "none" }}>
                    {i + 1}
                  </div>
                </motion.button>
              ))}
            </div>
          </Surface>

          {/* findings list */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-secondary mb-2 px-1">Findings ({pins.length}/{findings.length} pinned)</div>
            {findings.length === 0 ? (
              <Surface className="p-4 text-sm text-secondary">No adverse findings to pin — the report is all clear.</Surface>
            ) : (
              <div className="space-y-2">
                {findings.map((f, i) => {
                  const pin = pins.find((p) => p.item_key === f.item_key);
                  const active = selected === f.item_key;
                  return (
                    <Surface key={f.item_key} className="p-3" data-testid={`finding-${f.item_key}`}
                      style={{ outline: active ? "2px solid var(--accent)" : "none" }}>
                      <button className="w-full text-left" onClick={() => setSelected(active ? null : f.item_key)}>
                        <div className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: SEV_COLOR[f.severity] }} />
                          <span className="text-sm font-medium flex-1" style={{ color: "var(--text)" }}>{f.room} — {f.label}</span>
                          {pin ? <Pill tone="success">pinned</Pill> : <Pill tone="neutral">{active ? "tap plan" : "unpinned"}</Pill>}
                        </div>
                      </button>
                      {pin && (
                        <div className="flex items-center justify-between gap-2 mt-2">
                          <Segmented testid={`wall-${f.item_key}`} value={pin.wall} onChange={(w) => setWall(f.item_key, w)} options={WALLS} />
                          <button onClick={() => removePin(f.item_key)} className="p-1.5 rounded-lg" style={{ background: "color-mix(in srgb, var(--danger) 14%, transparent)" }}>
                            <Trash2 size={13} style={{ color: "var(--danger)" }} />
                          </button>
                        </div>
                      )}
                    </Surface>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
